import * as H from 'history';
import React, { createContext, useEffect, useMemo, useState } from 'react';
import { match } from 'react-router-dom';
import TransferApplicationFormPage from '../pages/TransferApplicationFormPage';
import { IApplication } from '../types';
import Axios from 'axios';
import { toast } from 'react-toastify';
import { API_URL } from '../../../../lib/const';
import TransferApplicationHeader from './TransferApplicationHeader';
import TransferApplicationStepper from './TransferApplicationStepper';
import TransferApplicationStep from './TransferApplicationStep';
import TransferApplicationSummaryPage from '../pages/TransferApplicationSummaryPage';
import consts from '../consts';
import { IDocumentType } from '../../common/types';
import { Spinner } from '../../../elements';
import TransferApplicationDocumentFormPage from '../pages/TransferApplicationDocumentFormPage';
import TransferApplicationApprovingPage from '../pages/TransferApplicationApprovingPage';
import TransferApplicationDeadlinePassedPage from '../pages/TransferApplicationDeadlinePassedPage';

type TransferApplicationRouteProps = {
  history: H.History,
  location: H.Location,
  match: match<{ token: string }>,
}

const initialValues: { application: IApplication } = {
  application: {
    id: -1,
    desired_onboarding: { id: 0, field_step: 0 },
    phone: '',
    real_name: '',
    birth_date: '',
    email: '',
    cpf: '',
    token: '',
    issues: [],
    process: {
      id: 0,
      name: '',
      course: {
        id: 0,
        name: ''
      }
    },
    documents: [],
    status: 'Não preenchido',
    personal_step_status: 'Não preenchido',
    documentation_step_status: 'Não preenchido',
    academic_step_status: 'Não preenchido',
    curriculum_adaptation_document_type: {
      id: -1,
      name: 'Adaptação Curricular'
    }
  },
}

const TransferApplicationProvider = ({ history, match, location }: TransferApplicationRouteProps) => {
  const [loading, setLoading] = useState({ application: true, documents: true });
  const [application, setApplication] = useState<IApplication>(initialValues.application);
  const [requestedDocuments, setRequestedDocuments] = useState<IDocumentType[]>([]);
  const token = useMemo(() => match.params.token, [match.params.token]);
  const newApplication = token === 'new';

  const statuses = useMemo(() => {
    const value = {
      personal: application.personal_step_status,
      documentation: application.documentation_step_status,
      summary: application.documentation_step_status,
      approving: application.status,
    }

    if (['Não preenchido', 'Preenchido'].includes(application.status)) {
      value.summary = 'Não preenchido';
      value.approving = 'Não preenchido';
    }

    return value;
  }, [application])

  // FIXME: Melhorar essas lógica para usar redirect no lugar de retornar os componentes toda hora.
  const currentStep = useMemo(() => {
    const fillingStatuses = ['Não preenchido', 'Preenchendo', 'Com pendências'];
    const deadlinePassed = !!application.submission_deadline_iso
      && new Date(application.submission_deadline_iso) < new Date();
    const stillFilling = fillingStatuses.includes(application.personal_step_status)
      || fillingStatuses.includes(application.documentation_step_status);

    if (deadlinePassed && stillFilling) {
      return {
        ...consts.TransferApplicationStep.DOCUMENTATION,
        status: statuses.documentation,
        component: <TransferApplicationDeadlinePassedPage application={application} />,
      };
    }

    if (['Em análise', 'Aprovado', 'Reprovado', 'Eliminado'].includes(application.status) || application.status === 'Com pendências' && application.academic_step_status === 'Com pendências') {
      return {
        ...consts.TransferApplicationStep.APPROVING,
        status: statuses.approving,
        component: (
          <TransferApplicationApprovingPage
            history={history}
            match={match}
            location={location}
            application={application}
            requestedDocuments={requestedDocuments}
            setApplication={setApplication}
          />
        ),
      }
    }

    if (['Com pendências', 'Não preenchido'].includes(application.personal_step_status) && match.path === '/transferencias/aplicacao/:token') {
      return {
        ...consts.TransferApplicationStep.PERSONAL_DATA,
        status: statuses.personal,
        component: (
          <TransferApplicationFormPage
            history={history}
            match={match}
            location={location}
            application={application}
            requestedDocuments={requestedDocuments}
            setApplication={setApplication}
          />
        ),
      }
    }

    if (['Com pendências', 'Não preenchido'].includes(application.documentation_step_status) && match.path === '/transferencias/aplicacao/:token') {
      return {
        ...consts.TransferApplicationStep.DOCUMENTATION,
        status: statuses.documentation,
        component: (
          <TransferApplicationDocumentFormPage
            history={history}
            match={match}
            location={location}
            application={application}
            requestedDocuments={requestedDocuments}
            loadingDocuments={loading.documents}
            setApplication={setApplication}
          />
        ),
      }
    }

    if (match.path === '/transferencias/aplicacao/:token/pessoais') {
      return {
        ...consts.TransferApplicationStep.PERSONAL_DATA,
        status: statuses.personal,
        component: (
          <TransferApplicationFormPage
            history={history}
            match={match}
            location={location}
            application={application}
            requestedDocuments={requestedDocuments}
            setApplication={setApplication}
          />
        ),
      }
    }

    if (match.path === '/transferencias/aplicacao/:token/documentos') {
      return {
        ...consts.TransferApplicationStep.DOCUMENTATION,
        status: statuses.documentation,
        component: (
          <TransferApplicationDocumentFormPage
            history={history}
            match={match}
            location={location}
            application={application}
            requestedDocuments={requestedDocuments}
            loadingDocuments={loading.documents}
            setApplication={setApplication}
          />
        ),
      }
    }

    if (match.path === '/transferencias/aplicacao/:token/resumo') {
      return {
        ...consts.TransferApplicationStep.SUMMARY,
        status: statuses.summary,
        component: (
          <TransferApplicationSummaryPage
            history={history}
            match={match}
            location={location}
            application={application}
            requestedDocuments={requestedDocuments}
            setApplication={setApplication}
          />
        ),
      }
    }

    return {
      ...consts.TransferApplicationStep.PERSONAL_DATA,
      status: statuses.personal,
      component: (
        <TransferApplicationFormPage
          history={history}
          match={match}
          location={location}
          application={application}
          requestedDocuments={requestedDocuments}
          setApplication={setApplication}
        />
      ),
    }

  }, [application, requestedDocuments, loading.documents, match])

  const steps = useMemo(() => {
    const statuses = {
      personal: application.personal_step_status,
      documentation: application.documentation_step_status,
      summary: application.status,
      approving: application.status,
    }

    if (['Não preenchido', 'Preenchido'].includes(application.status)) {
      statuses.summary = 'Não preenchido';
      statuses.approving = 'Não preenchido';
    }

    return (
      [
        {
          ...consts.TransferApplicationStep.PERSONAL_DATA,
          active: currentStep.id === consts.TransferApplicationStep.PERSONAL_DATA.id,
          status: statuses.personal,
        },
        {
          ...consts.TransferApplicationStep.DOCUMENTATION,
          active: currentStep.id === consts.TransferApplicationStep.DOCUMENTATION.id,
          status: statuses.documentation,
        },
        {
          ...consts.TransferApplicationStep.SUMMARY,
          active: currentStep.id === consts.TransferApplicationStep.SUMMARY.id,
          status: statuses.documentation,
        },
        {
          ...consts.TransferApplicationStep.APPROVING,
          active: currentStep.id === consts.TransferApplicationStep.APPROVING.id,
          status: statuses.approving,
        }
      ]
    )
  }, [application])

  useEffect(() => {
    if (!token) return

    Axios.get(`${API_URL}/transfers/applications/${token}`)
      .then(response => {
        setApplication(response.data);
        setLoading((v) => ({ ...v, application: false }));
      })
      .catch(() => history.push('/transferencias/aplicacao/new'))

  }, [token])

  useEffect(() => {
    if (!!!loading.application && application) {
      Axios.get(`${API_URL}/transfers/processes/${application.process.id}/required_documents`)
        .then(response => {
          setRequestedDocuments(response.data);
          setLoading((v) => ({ ...v, documents: false }));
        })
        // Sem o catch a etapa de Documentações ficava carregando para sempre e o
        // candidato não tinha nenhum sinal do que aconteceu.
        .catch(() => {
          setLoading((v) => ({ ...v, documents: false }));
          toast.error('Não foi possível carregar os documentos solicitados. Recarregue a página.');
        })
    }
  }, [application && application.id, loading.application])

  return (
    <Spinner active={newApplication ? (loading.application || loading.documents) : false}>
      <TransferApplicationHeader />
      <div className="content-top">
        <div className="container">
          <TransferApplicationStepper steps={steps} />
          <TransferApplicationStep
            number={currentStep.id}
            label={currentStep.label}
            description={currentStep.description}
            status={currentStep.status}
          >
            {currentStep.component}
          </TransferApplicationStep>
        </div>
      </div>
    </Spinner>
  )
}

export default TransferApplicationProvider;
